// Global search: one keystroke to any table, row, page, site or project.
//
// Built without cmdk or a Radix dialog, matching the policy stated in
// `components/ui/combobox.tsx`: this app is on the inlined-shadcn approach and
// does not pull new primitives for one surface. That means the keyboard and
// focus behaviour is ours to get right, so it is written out explicitly below
// rather than assumed.
//
// The index is fetched ONCE and filtered in the browser. `admin/pages/all`
// accepts a `search` param, but using it would mean a round-trip on every
// character. It is affordable to hold everything client-side only because
// `admin/search/index` drops page bodies: the same 483 pages are 100KB there
// versus 21.5MB from `admin/pages/all`.

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueries, useQuery } from "@tanstack/react-query";
import { FileText, FolderOpen, Globe, Rows3, Search, Table2, CornerDownLeft } from "lucide-react";
import { cn } from "@/lib/utils";
import { StatusPill } from "@/components/ui/status";
import { bloxxData, type TableDef, type Workspace } from "@/lib/bloxxData";
import { useScope } from "@/lib/scope";
import type { SearchIndex } from "@/lib/xano";
import { isTrashedPage } from "@/lib/pageTrash";

type ResultKind = "page" | "site" | "project" | "table" | "row";

interface Result {
  key: string;
  kind: ResultKind;
  label: string;
  sublabel: string;
  to: string;
  /** Lower is better. Used only to order within a group. */
  rank: number;
  badge?: { text: string; tone: "neutral" | "warning" };
}

const KIND_ICON = {
  page: FileText,
  site: Globe,
  project: FolderOpen,
  table: Table2,
  row: Rows3,
} as const;

const KIND_LABEL = {
  page: "Pages",
  site: "Sites",
  project: "Projects",
  table: "Tables",
  row: "Rows",
} as const;

/** Enough to be useful, few enough to stay keyboard-navigable without paging. */
const MAX_PER_GROUP = 6;

/** "1 project", not "1 projects". */
function count(n: number | undefined, noun: string): string {
  const v = n ?? 0;
  return `${v} ${noun}${v === 1 ? "" : "s"}`;
}

/**
 * Substring match, ranked by where the match lands.
 *
 * A prefix match beats a match in the middle, so typing "pri" puts "Pricing"
 * above "Enterprise pricing FAQ". Deliberately not fuzzy: fuzzy matching on
 * hundreds of similar slugs surfaces confident nonsense, and a user who typed
 * an exact word expects to see it first.
 */
function score(haystack: string, needle: string): number | null {
  const i = haystack.toLowerCase().indexOf(needle);
  return i === -1 ? null : i;
}

function build(index: SearchIndex | undefined, query: string): Result[] {
  if (!index) return [];
  const q = query.trim().toLowerCase();
  if (!q) return [];

  const domainById = new Map(index.sites.map((s) => [s.id, s.domain]));
  const out: Result[] = [];

  for (const s of index.sites) {
    const r = Math.min(
      score(s.name || "", q) ?? Infinity,
      score(s.domain || "", q) ?? Infinity
    );
    if (r !== Infinity) {
      out.push({
        key: `site-${s.id}`,
        kind: "site",
        label: s.name || s.domain,
        sublabel: s.domain,
        to: `/sites/${s.id}`,
        rank: r,
        badge: s.active ? undefined : { text: "inactive", tone: "neutral" },
      });
    }
  }

  for (const p of index.pages) {
    if (isTrashedPage(p)) continue; // deleted pages are still rows in the index (lib/pageTrash.ts)
    const domain = domainById.get(p.site_id) ?? "";
    const r = Math.min(
      score(p.title || "", q) ?? Infinity,
      score(p.slug || "", q) ?? Infinity,
      score(domain, q) ?? Infinity
    );
    if (r !== Infinity) {
      out.push({
        key: `page-${p.id}`,
        kind: "page",
        label: p.title || p.slug,
        sublabel: `${domain}/${p.slug}`,
        to: `/sites/${p.site_id}/pages/${p.id}/edit`,
        rank: r,
        // Draft and noindex are the two states where opening the page and
        // wondering why it is not live is the likely next move.
        badge: !p.published
          ? { text: "draft", tone: "neutral" }
          : p.noindex
            ? { text: "noindex", tone: "warning" }
            : undefined,
      });
    }
  }

  for (const pr of index.projects) {
    const r = score(pr.name || "", q);
    if (r !== null) {
      out.push({
        key: `project-${pr.id}`,
        kind: "project",
        label: pr.name,
        sublabel: pr.description || "Project",
        to: `/projects/${pr.id}`,
        rank: r,
      });
    }
  }

  return out;
}

/**
 * A row's snippet, which arrives MARKED UP AND NOT AS PROSE.
 *
 * `/search` returns the matching text with every hit wrapped in square brackets
 * and the columns joined by " \n " — e.g.
 *   "[Northwind] Supplies \n Industrial supplies wholesaler in Setubal."
 * Rendered raw that is a row of stray brackets and a stray backslash-n, which to
 * anybody who has only used Google Sheets reads as the app being broken. The
 * brackets are not noise though: they are the only thing that says WHY this row
 * came back, which for a match in the fourth column is the whole answer. So they
 * become emphasis, and the separator becomes a middot.
 *
 * Falls back to the raw string if the markers are unbalanced — a snippet with a
 * literal bracket in the data is worth showing plainly rather than mangling.
 */
function Snippet({ text }: { text: string }) {
  const flat = text.replace(/\s*\\n\s*|\s*\n\s*/g, " · ").trim();
  const parts = flat.split(/\[([^\]]*)\]/g);
  if (parts.length === 1) return <>{flat}</>;
  return (
    <>
      {parts.map((p, i) =>
        // split() with one capture group alternates: text, match, text, match…
        i % 2 === 1 ? (
          <mark key={i} className="bg-transparent font-medium text-foreground">
            {p}
          </mark>
        ) : (
          <span key={i}>{p}</span>
        ),
      )}
    </>
  );
}

/**
 * Tables, matched on their name. Cheap: a workspace's table list is a handful of
 * rows and is usually already cached by the Tables screen.
 */
function buildTables(
  lists: { workspace: Workspace; tables: TableDef[] }[],
  query: string,
  showWorkspace: boolean,
): Result[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const out: Result[] = [];
  for (const { workspace, tables } of lists) {
    for (const t of tables) {
      const r = score(t.name || "", q);
      if (r === null) continue;
      out.push({
        key: `table-${workspace.id}-${t.id}`,
        kind: "table",
        label: t.name,
        // The workspace earns its place only when more than one is in view.
        sublabel: showWorkspace ? `${workspace.name} · ${t.item_type}` : String(t.item_type),
        to: `/tables/${workspace.id}/${t.id}`,
        rank: r,
      });
    }
  }
  return out;
}

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const navigate = useNavigate();
  const { inScope, isAll } = useScope();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  /** Where focus was before opening, so Escape puts it back. */
  const restoreRef = useRef<HTMLElement | null>(null);

  /**
   * `openedOnce` gates the fetch. A user who never presses the shortcut should
   * never pay for the index, so this costs nothing until it is first used, and
   * nothing again afterwards.
   */
  const [openedOnce, setOpenedOnce] = useState(false);

  const indexQ = useQuery<SearchIndex>({
    queryKey: ["search-index"],
    queryFn: async () => (await import("@/lib/xano")).xano.search.index(),
    enabled: openedOnce,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        restoreRef.current = document.activeElement as HTMLElement | null;
        setOpenedOnce(true);
        setOpen((v) => !v);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  /**
   * THE DATA HALF OF "SEARCH EVERYTHING".
   *
   * The palette used to cover pages, sites and projects — everything in Xano —
   * and nothing at all in the data layer, so the answer to "where is that table"
   * was still to go and look. Two separate services, and no reason a person
   * should have to know which one their thing is in.
   *
   * Both reads are SCOPED BY THE TOP-BAR SELECTOR, which is what makes "All
   * workspaces" mean something concrete rather than being a label. Rows come from
   * the control plane's denormalised index, which is already cross-workspace and
   * already filtered by membership in the query — a Durable Object can only see
   * its own database, so this is the one thing the data plane structurally cannot
   * answer for itself.
   */
  const tableQs = useQueries({
    queries: (openedOnce ? inScope : []).map((w) => ({
      queryKey: ["data", "tables", w.id],
      queryFn: () => bloxxData.listTables(w.id),
      staleTime: 5 * 60 * 1000,
      retry: false,
    })),
  });

  /**
   * DEBOUNCED, unlike everything else in this palette.
   *
   * Pages, sites and projects are one fetch filtered in the browser, so typing
   * costs nothing. Rows cannot work that way — the index is server-side and spans
   * every workspace — so a query key straight off `query` would be one request
   * per keystroke: seven for "members", six of them already stale when they land.
   */
  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(query.trim()), 180);
    return () => window.clearTimeout(t);
  }, [query]);

  const rowsQ = useQuery({
    queryKey: ["data", "search", debounced],
    queryFn: () => bloxxData.search(debounced),
    // Two characters, like the rail's own row search: one letter matches
    // everything, and the request is wasted before it lands.
    enabled: openedOnce && debounced.length > 1,
    staleTime: 30_000,
    retry: false,
  });

  const results = useMemo(() => {
    const out = build(indexQ.data, query);

    const lists = inScope.map((w, i) => ({ workspace: w, tables: tableQs[i]?.data?.tables ?? [] }));
    out.push(...buildTables(lists, query, isAll && inScope.length > 1));

    const allowed = new Set(inScope.map((w) => w.id));
    for (const h of rowsQ.data?.hits ?? []) {
      // The row index spans every workspace you are a member of, so a narrowed
      // scope has to filter it here — the server has no idea what you picked.
      if (!allowed.has(h.workspace_id)) continue;
      out.push({
        key: `row-${h.table_id}-${h.row_id}`,
        kind: "row",
        label: h.title || "Untitled row",
        sublabel: h.snippet || h.item_type,
        // Opens the table the row is in. Deep-linking to the record itself needs
        // a row param the grid does not read yet; landing in the right table
        // beats not offering the row at all.
        to: `/tables/${h.workspace_id}/${h.table_id}`,
        // Ranked BELOW every name match on purpose: a hit inside a cell is a
        // weaker signal than a thing actually called what you typed.
        rank: 1000,
      });
    }
    return out;
  }, [indexQ.data, query, inScope, tableQs, isAll, rowsQ.data]);

  /** Grouped, each group capped, but the flat order is what the keyboard walks. */
  const groups = useMemo(() => {
    const order: ResultKind[] = ["table", "row", "page", "site", "project"];
    return order
      .map((kind) => ({
        kind,
        items: results
          .filter((r) => r.kind === kind)
          .sort((a, b) => a.rank - b.rank || a.label.localeCompare(b.label))
          .slice(0, MAX_PER_GROUP),
      }))
      .filter((g) => g.items.length > 0);
  }, [results]);

  const flat = useMemo(() => groups.flatMap((g) => g.items), [groups]);

  // Clamped rather than reset: as the list shrinks under a longer query, the
  // highlight must stay on a row that exists or Enter navigates nowhere.
  useEffect(() => {
    setActive((a) => (flat.length === 0 ? 0 : Math.min(a, flat.length - 1)));
  }, [flat.length]);

  useEffect(() => {
    if (open) {
      setActive(0);
      // Focus after paint, or the input is not in the document yet.
      const t = window.setTimeout(() => inputRef.current?.focus(), 0);
      return () => window.clearTimeout(t);
    }
    setQuery("");
    restoreRef.current?.focus?.();
  }, [open]);

  // Keep the highlighted row visible when arrowing past the fold.
  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector<HTMLElement>(`[data-idx="${active}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [active, open]);

  function go(r: Result | undefined) {
    if (!r) return;
    setOpen(false);
    navigate(r.to);
  }

  function onInputKey(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => (flat.length === 0 ? 0 : (a + 1) % flat.length));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => (flat.length === 0 ? 0 : (a - 1 + flat.length) % flat.length));
    } else if (e.key === "Enter") {
      e.preventDefault();
      go(flat[active]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
    }
  }

  if (!open) return null;

  const activeId = flat[active] ? `cp-opt-${flat[active].key}` : undefined;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-foreground/20 p-4 pt-[10vh]"
      // Clicking the backdrop closes. The inner panel stops propagation so a
      // click inside never does.
      onMouseDown={() => setOpen(false)}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Search tables, rows, pages, sites and projects"
        className="w-full max-w-xl overflow-hidden rounded-xl border bg-background shadow-2xl"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 border-b px-3">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onInputKey}
            placeholder="Search tables, rows, pages, sites and projects"
            aria-label="Search tables, rows, pages, sites and projects"
            role="combobox"
            aria-expanded
            aria-controls="cp-listbox"
            aria-activedescendant={activeId}
            className="h-12 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
          <kbd className="hidden shrink-0 rounded border px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground sm:inline">
            esc
          </kbd>
        </div>

        <div ref={listRef} id="cp-listbox" role="listbox" className="max-h-[50vh] overflow-y-auto p-2">
          {indexQ.isLoading ? (
            <p className="px-2 py-6 text-center text-sm text-muted-foreground">Loading…</p>
          ) : indexQ.isError ? (
            // Same rule as the stat tiles: a failed request must not look like
            // "nothing matched".
            <p className="px-2 py-6 text-center text-sm text-muted-foreground">
              Could not load the search index.
            </p>
          ) : !query.trim() ? (
            <div className="px-2 py-6 text-center text-sm text-muted-foreground">
              <p>
                Type to search {count(indexQ.data?.pages.length, "page")},{" "}
                {count(indexQ.data?.sites.length, "site")},{" "}
                {count(indexQ.data?.projects.length, "project")} and your tables.
              </p>
              {/* SAYS WHAT IT IS AND IS NOT COVERING. A narrowed scope silently
                  hiding results is indistinguishable from the results not
                  existing, which is the worst thing a search box can do. */}
              {!isAll && (
                <p className="mt-1 text-xs">
                  Tables and rows in one workspace only. Switch to All workspaces to search
                  everything.
                </p>
              )}
            </div>
          ) : flat.length === 0 ? (
            <p className="px-2 py-6 text-center text-sm text-muted-foreground">
              Nothing matches “{query.trim()}”.
            </p>
          ) : (
            groups.map((g) => {
              const Icon = KIND_ICON[g.kind];
              const total = results.filter((r) => r.kind === g.kind).length;
              return (
                <div key={g.kind} className="mb-2 last:mb-0">
                  <div className="flex items-baseline justify-between px-2 py-1">
                    <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                      {KIND_LABEL[g.kind]}
                    </span>
                    {/* Says when the list is truncated. A silently capped list
                        makes a missing result look like a missing record. */}
                    {total > g.items.length && (
                      <span className="text-[11px] text-muted-foreground">
                        showing {g.items.length} of {total}
                      </span>
                    )}
                  </div>
                  {g.items.map((r) => {
                    const idx = flat.indexOf(r);
                    const isActive = idx === active;
                    return (
                      <div
                        key={r.key}
                        id={`cp-opt-${r.key}`}
                        data-idx={idx}
                        role="option"
                        aria-selected={isActive}
                        onMouseMove={() => setActive(idx)}
                        onClick={() => go(r)}
                        className={cn(
                          "flex cursor-pointer items-center gap-3 rounded-lg px-2 py-2",
                          isActive && "bg-secondary"
                        )}
                      >
                        <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm">{r.label}</div>
                          <div className="truncate text-xs text-muted-foreground">
                            {r.kind === "row" ? <Snippet text={r.sublabel} /> : r.sublabel}
                          </div>
                        </div>
                        {r.badge && <StatusPill tone={r.badge.tone}>{r.badge.text}</StatusPill>}
                        {isActive && (
                          <CornerDownLeft className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

/** Top-bar affordance. The shortcut is useless if nobody knows it exists. */
export function CommandPaletteTrigger() {
  const isMac =
    typeof navigator !== "undefined" && /mac|iphone|ipad/i.test(navigator.platform || "");

  function open() {
    // Dispatched rather than lifted into shared state: the palette already owns
    // this shortcut, so re-using it keeps one code path for opening.
    window.dispatchEvent(
      new KeyboardEvent("keydown", { key: "k", metaKey: true, ctrlKey: !isMac, bubbles: true })
    );
  }

  return (
    <button
      type="button"
      onClick={open}
      aria-label="Search tables, rows, pages, sites and projects"
      className="flex h-8 items-center gap-2 rounded-md border px-2.5 text-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
    >
      <Search className="h-3.5 w-3.5" />
      <span className="hidden sm:inline">Search</span>
      <kbd className="hidden font-mono text-[10px] sm:inline">{isMac ? "⌘K" : "Ctrl K"}</kbd>
    </button>
  );
}
